import {
  Box,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import type { ReactNode, Ref } from 'react'

import IwiPicker from '@/components/IwiPicker'
import LocationSelector, {
  SearchLocationsStatus,
  isNational,
  selectedRegionLabels,
} from '@/components/LocationSelector'
import FilterBox, { PreviewLine } from './FilterBox'
import IncludeExcludeToggle from './IncludeExcludeToggle'
import { BORDER, ERROR_TEXT, MUTED_TEXT, NAVY } from './styles'
import {
  DEFAULT_SEARCH_FILTERS,
  type DatabaseSearchFilters,
  type RoundTiming,
} from '@/models/DatabaseSearch'
import type { IwiData } from '@/models/Iwi'
import type { RegionData, RegionSetData } from '@/models/Regions'

export const INTERNATIONAL_TOOLTIP =
  'Funding Opportunities from Outside of New Zealand'
export const GAMBLING_TOOLTIP = 'Proceeds from Lotteries, Pokies, and Casinos'
export const RELATIONAL_TOOLTIP =
  "Relational opportunities are with Funders who don't have an open grants process, but are open to being approached."

const TIMING_OPTIONS: { value: RoundTiming; label: string }[] = [
  { value: 'now', label: 'Open now' },
  { value: 'soon', label: 'Open now or in the next 3 months' },
  { value: 'any', label: 'Any time' },
]

interface SearchFilterColumnProps {
  filters: DatabaseSearchFilters
  onChange: (patch: Partial<DatabaseSearchFilters>) => void
  regions: RegionData[]
  regionSets: RegionSetData[]
  iwiList: IwiData[] | undefined
  iwiLoading?: boolean
  iwiError?: string | null
  /** Focused when Search is refused for want of an iwi. */
  iwiInputRef?: Ref<HTMLInputElement>
  locationsOpen: boolean
  onLocationsOpenChange: (open: boolean) => void
  /** The Keywords & tags section, rendered last. */
  keywordsAndTags?: ReactNode
}

/** A filter group's heading, with a divider above it. */
export function Heading({ children, first = false }: { children: ReactNode; first?: boolean }) {
  return (
    <>
      <Divider sx={{ borderColor: BORDER, mt: first ? 0 : 3.5, mb: 3 }} />
      <Typography
        variant="h6"
        component="h2"
        sx={{ fontSize: '18px', fontWeight: 500, color: NAVY, mb: 0.5 }}
      >
        {children}
      </Typography>
    </>
  )
}

const amountText = (amount: number | null) => (amount === null ? '' : String(amount))

/** The filter a typed minimum applies: null for blank or negative. */
function parseMinAmount(raw: string): number | null {
  const trimmed = raw.trim()
  const amount = Number(trimmed)
  return trimmed === '' || !Number.isFinite(amount) || amount < 0 ? null : amount
}

function Helper({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <Typography id={id} sx={{ fontSize: '14px', color: MUTED_TEXT, mb: 1 }}>
      {children}
    </Typography>
  )
}

export default function SearchFilterColumn({
  filters,
  onChange,
  regions,
  regionSets,
  iwiList,
  iwiLoading = false,
  iwiError = null,
  iwiInputRef,
  locationsOpen,
  onLocationsOpenChange,
  keywordsAndTags,
}: SearchFilterColumnProps) {
  const nationalIds = new Set(regions.filter(isNational).map((r) => r.id))
  const hasLocalSelection = filters.region_ids.some((id) => !nationalIds.has(id))
  const locationLabels = selectedRegionLabels(
    regions,
    regionSets,
    filters.region_ids,
  )

  // The field keeps its own text so a rejected negative stays on screen while
  // the filter holds null. It resyncs when the filter changes from outside,
  // including Reset, which sets null over a negative that already held null.
  const [minText, setMinText] = useState(() => amountText(filters.min_amount))
  const [seenFilters, setSeenFilters] = useState(filters)
  if (filters !== seenFilters) {
    setSeenFilters(filters)
    if (
      filters === DEFAULT_SEARCH_FILTERS ||
      filters.min_amount !== parseMinAmount(minText)
    ) {
      setMinText(amountText(filters.min_amount))
    }
  }
  const minInvalid = minText.trim() !== '' && Number(minText) < 0

  return (
    <Box component="form" onSubmit={(e) => e.preventDefault()} autoComplete="off">
      <Heading first>Locations</Heading>
      <SearchLocationsStatus hasLocalSelection={hasLocalSelection} />
      <FilterBox
        noun="locations"
        open={locationsOpen}
        onOpenChange={onLocationsOpenChange}
        preview={
          locationLabels.length > 0 ? (
            <PreviewLine label="Selected locations" chips={locationLabels} />
          ) : null
        }
      >
        <LocationSelector
          mode="search"
          regions={regions}
          regionSets={regionSets}
          selectedIds={filters.region_ids}
          onChange={(ids) => onChange({ region_ids: ids })}
        />
      </FilterBox>

      <Heading>Funding preferences</Heading>
      <IncludeExcludeToggle
        label="International funding"
        tooltip={INTERNATIONAL_TOOLTIP}
        value={filters.international}
        onChange={(value) => onChange({ international: value })}
      />
      <IncludeExcludeToggle
        label="Gambling proceeds"
        tooltip={GAMBLING_TOOLTIP}
        value={filters.gambling}
        onChange={(value) => onChange({ gambling: value })}
      />
      <IncludeExcludeToggle
        label="Relational opportunities"
        tooltip={RELATIONAL_TOOLTIP}
        value={filters.relational}
        onChange={(value) => onChange({ relational: value })}
      />
      <FormControlLabel
        sx={{ mt: 0.5 }}
        control={
          <Checkbox
            checked={filters.iwi_eligible}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? { iwi_eligible: true }
                  : { iwi_eligible: false, iwi_ids: [] },
              )
            }
            sx={{ '&.MuiCheckbox-colorPrimary': { color: '#1C3045' } }}
          />
        }
        label="Potentially eligible for iwi funding"
      />
      {filters.iwi_eligible && (
        <Box sx={{ ml: 4 }}>
          <IwiPicker
            iwiList={iwiList}
            iwiLoading={iwiLoading}
            selectedIds={filters.iwi_ids}
            onChange={(ids) => onChange({ iwi_ids: ids })}
            ariaLabel="Iwi"
            error={Boolean(iwiError)}
            errorId="iwi-error"
            inputRef={iwiInputRef}
          />
          {/* Read through the input's aria-describedby: a refused Search
              moves focus to the input. */}
          <Typography id="iwi-error" sx={{ color: ERROR_TEXT, fontSize: '14px' }}>
            {iwiError}
          </Typography>
        </Box>
      )}

      <Heading>Minimum grant size</Heading>
      <Helper id="min-grant-help">
        The smallest grant you would bother applying for.{' '}
        <strong>Enter your minimum, not your project total.</strong> Leave blank
        to show any amount.
      </Helper>
      <TextField
        type="number"
        size="small"
        value={minText}
        onChange={(e) => {
          setMinText(e.target.value)
          onChange({ min_amount: parseMinAmount(e.target.value) })
        }}
        // A wheel over a focused number field steps its value; blurring
        // first lets the wheel scroll the page instead.
        onWheel={(e) => {
          if (e.target instanceof HTMLInputElement) e.target.blur()
        }}
        error={minInvalid}
        helperText={minInvalid ? 'Enter 0 or more.' : undefined}
        slotProps={{
          formHelperText: { id: 'min-grant-error' },
          htmlInput: {
            min: 0,
            step: 1,
            'aria-label': 'Minimum grant size',
            'aria-describedby': minInvalid ? 'min-grant-help min-grant-error' : 'min-grant-help',
            'aria-invalid': minInvalid,
          },
          input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
        }}
        sx={{ bgcolor: '#fff', width: '100%' }}
      />

      <Heading>Round timing</Heading>
      <TextField
        select
        size="small"
        value={filters.timing}
        onChange={(e) => onChange({ timing: e.target.value as RoundTiming })}
        inputProps={{ 'aria-label': 'Round timing' }}
        sx={{ bgcolor: '#fff', width: '100%' }}
      >
        {TIMING_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {keywordsAndTags}
    </Box>
  )
}
